// Shared helpers for the Projects & Service Providers module (admin + provider portal).
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export const num = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export const thisMonth = () => new Date().toISOString().slice(0, 7);

export const todayStr = () => {
  // IST calendar date, which is what the backend validates against.
  const d = new Date(Date.now() + 330 * 60000);
  return d.toISOString().slice(0, 10);
};

// Fetch a protected file (PDF / Excel / agreement) with the session cookie and save it.
export async function downloadFile(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob", timeout: 60000 });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 2000);
  } catch (e) {
    let detail = e.response?.data?.detail;
    if (e.response?.data instanceof Blob) {
      try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* keep default */ }
    }
    toast.error(apiError(detail) || "Download failed");
  }
}

// Open a protected image (field-log photo) in a new tab.
export async function openImage(url) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    window.open(URL.createObjectURL(res.data), "_blank");
  } catch {
    toast.error("Could not load the photo");
  }
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Phone photos are often 4–8 MB; shrink to ~1600px JPEG before upload so field
// workers on weak mobile data can still submit.
export async function compressImage(file, maxDim = 1600, quality = 0.8) {
  const dataUrl = await fileToBase64(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return dataUrl;
  }
}

export function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This device doesn't support location — the visit can't be geo-tagged."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy_m: pos.coords.accuracy }),
      (err) => reject(new Error(err.code === err.PERMISSION_DENIED
        ? "Location permission was denied. Please allow location access and try again."
        : "Couldn't get your location. Turn on GPS and try again.")),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
}

export const onErr = (e) => toast.error(apiError(e.response?.data?.detail) || e.message);
